import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { TabProvider, useTabContext } from 'components/Tabs/TabContext'
import { Tabs } from 'components/Tabs/Tabs'
import { HonestTable, IHonestTableColumn, IHonestTableMenuOption } from 'components/HonestTable/HonestTable'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import useQueryPagination from 'hooks/useQueryPagination'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText } from 'styling/Typography'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import {
  approveInventoryRequest,
  clearInventoryRequests,
  getInventoryRequests,
  issueInventoryRequest,
  routeInventoryRequestToProcurement,
} from 'features/inventory/inventoryRequestsSlice'
import { useNavigate } from 'react-router-dom'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { FilterEnum } from 'components/Filter/SingleFilter'
import { IInventoryRequestResponse } from 'types/api/inventoryRequestApi.interface'
import { IStoreServeRequestState } from 'types/inventoryCounter.interface'
import { ViewInventoryRequestModal } from './ViewInventoryRequestModal'
import { DeclineInventoryRequestModal } from './DeclineInventoryRequestModal'
import {
  getInventoryRequestStatusLabel,
  INVENTORY_REQUEST_STATUS_APPROVED,
  INVENTORY_REQUEST_STATUS_PENDING,
  inventoryRequestStatusOptions,
} from './inventoryRequestsData'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { counterLinesSummary } from '../Shared/inventoryFormatters'

const getInventoryRequestStatusTone = (status: number): IStatusPill['status'] => {
  switch (status) {
    case 2:
    case 4:
      return 'success'
    case 1:
      return 'warning'
    case 3:
      return 'info'
    case 5:
      return 'danger'
    default:
      return 'neutral'
  }
}

const buildServeRequestState = (item: IInventoryRequestResponse): IStoreServeRequestState => ({
  id: item.id,
  department: item.department?.name || '—',
  requesterUser: item.requestedByUser
    ? {
        value: item.requestedByUser.id,
        label: `${item.requestedByUser.firstName} ${item.requestedByUser.lastName}`,
      }
    : null,
  items: (item.lines || [])
    .filter((line) => line.inventoryItem)
    .map((line) => ({
      itemId: line.inventoryItem.id,
      name: line.inventoryItem.name,
      sku: line.inventoryItem.sku || null,
      quantity: line.quantity,
    })),
})

const InventoryRequestsContent = ({
  setParams,
  hasInventoryPermission,
  tabs,
}: {
  setParams: React.Dispatch<React.SetStateAction<any>>
  hasInventoryPermission: boolean
  tabs: string[]
}) => {
  const { requests, isLoading } = useSelector((store: RootState) => store.inventoryRequests)
  const { user } = useSelector((store: RootState) => store.user)
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { activeTab } = useTabContext()
  const [viewModal, setViewModal] = useState<{ open: boolean; value: IInventoryRequestResponse }>({
    open: false,
    value: null,
  })
  const [declineModal, setDeclineModal] = useState<{ open: boolean; value: IInventoryRequestResponse }>({
    open: false,
    value: null,
  })

  useEffect(() => {
    const listType = activeTab === 'All Requests' ? 'all' : 'my'
    setParams((prev: any) => {
      if (prev.listType === listType) return prev
      return { ...prev, page: 1, listType }
    })
  }, [activeTab, setParams])

  const refreshList = () => {
    setParams((prev: any) => ({ ...prev }))
  }

  const confirmAction = (word: string, onSave: () => Promise<void>) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave,
        setParams: {
          type: ModalTypes.BeforeSabe,
          f: setParams,
          noItems: requests.data.length < 2,
        },
        word,
      }),
    )
  }

  const getMenuOptions = (item: IInventoryRequestResponse): IHonestTableMenuOption<IInventoryRequestResponse>[] => {
    const options: IHonestTableMenuOption<IInventoryRequestResponse>[] = []
    const isRequester = item.requestedByUser?.id === user?.id

    if (hasInventoryPermission && item.status === INVENTORY_REQUEST_STATUS_PENDING) {
      options.push({
        label: 'Approve',
        onClick: () =>
          confirmAction('approve this request', async () => {
            await dispatch(approveInventoryRequest(item.id))
          }),
      })
      options.push({
        label: 'Decline',
        onClick: () => setDeclineModal({ open: true, value: item }),
      })
      options.push({
        label: 'Route to Procurement',
        onClick: () =>
          confirmAction('route this request to procurement', async () => {
            await dispatch(routeInventoryRequestToProcurement(item.id))
          }),
      })
    }

    if (isRequester && item.status === INVENTORY_REQUEST_STATUS_APPROVED) {
      options.push({
        label: 'Issue',
        onClick: () =>
          confirmAction('issue this request', async () => {
            await dispatch(issueInventoryRequest(item.id))
          }),
      })
    }

    if (hasInventoryPermission && item.status === INVENTORY_REQUEST_STATUS_APPROVED) {
      options.push({
        label: 'Serve at counter',
        onClick: () =>
          navigate('/inventory/counter', { state: { serveRequest: buildServeRequestState(item) } }),
      })
    }

    return options
  }

  const columns: IHonestTableColumn<IInventoryRequestResponse>[] = useMemo(
    () => [
      {
        id: 'ref',
        header: 'REF',
        cell: (item) => (
          <Cell>
            <BodyText>{`REQ-${item.id}`}</BodyText>
          </Cell>
        ),
      },
      {
        id: 'date',
        header: 'DATE',
        cell: (item) => (
          <Cell>
            <BodyText>{formatDateForDisplay(item.createdAt)}</BodyText>
          </Cell>
        ),
      },
      {
        id: 'requester',
        header: 'REQUESTER',
        cell: (item) => (
          <Cell>
            {item.requestedByUser ? (
              <BodyText>
                {`${item.requestedByUser.firstName} ${item.requestedByUser.lastName}`}
              </BodyText>
            ) : (
              <OtherText grey>—</OtherText>
            )}
          </Cell>
        ),
      },
      {
        id: 'department',
        header: 'DEPARTMENT',
        cell: (item) => (
          <Cell>
            {item.department?.name ? (
              <BodyText>{item.department.name}</BodyText>
            ) : (
              <OtherText grey>—</OtherText>
            )}
          </Cell>
        ),
      },
      {
        id: 'items',
        header: 'ITEMS',
        fill: true,
        cell: (item) => (
          <Cell>
            <BodyText>
              {counterLinesSummary(
                (item.lines || []).map((line) => ({
                  quantity: line.quantity,
                  name: line.inventoryItem?.name ?? 'Item',
                })),
              )}
            </BodyText>
          </Cell>
        ),
      },
      {
        id: 'costCenter',
        header: 'COST CENTER',
        cell: (item) => (
          <Cell>
            {item.costCenter?.name ? (
              <BodyText>{item.costCenter.name}</BodyText>
            ) : (
              <OtherText grey>—</OtherText>
            )}
          </Cell>
        ),
      },
      {
        id: 'status',
        header: 'STATUS',
        cell: (item) => (
          <Cell>
            <Flex center gap='0.5rem'>
              <StatusPill status={getInventoryRequestStatusTone(item.status)}>
                {getInventoryRequestStatusLabel(item.status)}
              </StatusPill>
              {item.status === INVENTORY_REQUEST_STATUS_APPROVED && (
                <StatusPill status='accent'>RESERVES STOCK</StatusPill>
              )}
            </Flex>
          </Cell>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <Flex between center mb='16'>
        <MainHeadline>Inventory Requests</MainHeadline>
        <Button variant='primary' onClick={() => navigate('add')}>
          Add Request
        </Button>
      </Flex>
      {hasInventoryPermission && tabs.length > 1 && (
        <Flex mb='16'>
          <Tabs />
        </Flex>
      )}
      {!isLoading && !requests?.data?.length ? (
        <NoItems title='Requests' />
      ) : (
        <HonestTable
          columns={columns}
          data={requests.data}
          keyField='id'
          loading={isLoading}
          onClick={(item) => setViewModal({ open: true, value: item })}
          menuOptions={(item) => getMenuOptions(item)}
          emptyMessage='No requests found'
        />
      )}
      {viewModal.open && (
        <ViewInventoryRequestModal
          value={viewModal.value}
          closeModal={() => setViewModal({ value: null, open: false })}
        />
      )}
      {declineModal.open && (
        <DeclineInventoryRequestModal
          value={declineModal.value}
          closeModal={() => setDeclineModal({ open: false, value: null })}
          onSuccess={refreshList}
        />
      )}
    </>
  )
}

export const InventoryRequests = () => {
  const { user } = useSelector((store: RootState) => store.user)
  const { requests } = useSelector((store: RootState) => store.inventoryRequests)
  const hasInventoryPermission = user?.currentContract?.role?.permissionPolicy?.inventory
  const tabs = hasInventoryPermission ? ['My Requests', 'All Requests'] : ['My Requests']
  const [params, setParams] = useState<any>({
    ...getParams({ location }),
    listType: 'my',
  })

  useQueryPagination({
    fetchFunctions: [getInventoryRequests],
    cleanUpFunctions: [clearInventoryRequests],
    params,
  })

  const filters = [
    {
      type: FilterEnum.SELECT,
      name: 'Status',
      options: inventoryRequestStatusOptions,
      propName: 'status',
    },
  ]

  return (
    <>
      <NavBar enableTemporaryManualClosing filterOptions={filters} visibleFilterOverflow setParams={setParams} />
      <PageWrapper>
        <TabProvider tabs={tabs} groupName='inventory-requests-tab'>
          <InventoryRequestsContent
            setParams={setParams}
            hasInventoryPermission={hasInventoryPermission}
            tabs={tabs}
          />
        </TabProvider>
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(requests?.totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
